import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api, logActivity } from '../api/client';
import WelcomeModal from './WelcomeModal';
import GuidedTour from './GuidedTour';
import { startTour, openWelcome, restoreChecklist, setCurrentUser } from '../lib/onboarding';
import { useLens } from '../hooks/useLens';

type NavItem = { path: string; label: string };
type NavGroup = { label: string; items: NavItem[] };

// BUYER lens (default) — the existing government-buyer experience, unchanged.
const BUYER_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Research',
    items: [
      { path: '/dashboard', label: 'Dashboard' },
      { path: '/sam-library', label: 'SAM.gov Library' },
      { path: '/vendors', label: 'Vendors' },
      { path: '/price-benchmark', label: 'Price Benchmark' },
      { path: '/alerts', label: 'Alerts' },
    ],
  },
  {
    label: 'Procurement',
    items: [
      { path: '/opportunities', label: 'Opportunities' },
      { path: '/cases', label: 'Cases' },
      { path: '/engagement', label: 'Engagement' },
    ],
  },
  {
    label: 'Execution',
    items: [
      { path: '/tasks', label: 'Tasks' },
    ],
  },
  {
    label: 'Support',
    items: [
      { path: '/support', label: 'Support' },
      { path: '/training', label: 'Workforce Training' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { path: '/ai-governance', label: 'AI Governance' },
      { path: '/security-readiness', label: 'Security' },
      { path: '/admin/users', label: 'Admin' },
      { path: '/admin/activity', label: 'Activity Log' },
      { path: '/admin/model-training', label: 'Model Training' },
      { path: '/admin/aws-usage', label: 'AWS Usage' },
    ],
  },
];

// SELLER lens — the vendor "pursue and win" experience. Reuses shared research
// surfaces (SAM library, price benchmark, vendors) and adds seller-specific pages.
const SELLER_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Discover',
    items: [
      // Profile first, then matches — the natural flow (build capabilities → get matches).
      { path: '/seller-onboarding', label: 'My Capabilities' },
      { path: '/match-me', label: 'Match Me' },
      // Discovery IS the SAM.gov notice search (seller-framed); no separate library entry.
      { path: '/discovery', label: 'Discovery (SAM.gov)' },
      { path: '/price-benchmark', label: 'Price Benchmark' },
      { path: '/alerts', label: 'Alerts' },
    ],
  },
  {
    label: 'Pipeline',
    items: [
      { path: '/opportunities', label: 'My Pursuits' },
      { path: '/bid-no-bid', label: 'Bid / No-Bid' },
    ],
  },
  {
    label: 'Teaming',
    items: [
      { path: '/teaming', label: 'Partner Matching' },
      { path: '/vendors', label: 'Vendors' },
    ],
  },
  {
    label: 'Support',
    items: [
      { path: '/support', label: 'Support' },
      { path: '/training', label: 'Workforce Training' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { path: '/ai-governance', label: 'AI Governance' },
      { path: '/security-readiness', label: 'Security' },
      { path: '/admin/users', label: 'Admin' },
      { path: '/admin/activity', label: 'Activity Log' },
      { path: '/admin/model-training', label: 'Model Training' },
      { path: '/admin/aws-usage', label: 'AWS Usage' },
    ],
  },
];

function navGroupsForLens(lens: 'buyer' | 'seller'): NavGroup[] {
  return lens === 'seller' ? SELLER_NAV_GROUPS : BUYER_NAV_GROUPS;
}

// Flat list for breadcrumb lookup (union of both lenses so labels resolve either way).
const NAV: NavItem[] = [
  ...BUYER_NAV_GROUPS.flatMap(g => g.items),
  { path: '/discovery', label: 'Discovery' },
  { path: '/match-me', label: 'Match Me' },
  { path: '/bid-no-bid', label: 'Bid / No-Bid' },
  { path: '/teaming', label: 'Partner Matching' },
];

const styles: Record<string, React.CSSProperties> = {
  shell: { display: 'flex', minHeight: '100vh', background: '#f4f6fa' },
  sidebar: { width: 240, background: 'linear-gradient(180deg, #0f1117 0%, #1a1d2e 100%)', color: 'white', display: 'flex', flexDirection: 'column', flexShrink: 0, borderRight: '1px solid rgba(255,255,255,0.06)' },
  logo: { padding: '24px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  logoTitle: { fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: -0.3 },
  logoSub: { fontSize: 11, color: '#818cf8', marginTop: 3, fontWeight: 500, letterSpacing: 0.5, textTransform: 'uppercase' as const },
  nav: { flex: 1, padding: '16px 12px' },
  navLink: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', fontSize: 13, color: 'rgba(255,255,255,0.6)', textDecoration: 'none', transition: 'all 0.2s', borderRadius: 10, marginBottom: 2, fontWeight: 500 },
  navLinkActive: { background: 'rgba(99,102,241,0.15)', color: 'white', backdropFilter: 'blur(10px)' },
  userBar: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: 12, color: 'rgba(255,255,255,0.5)' },
  signOut: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', padding: '7px 12px', borderRadius: 8, fontSize: 12, marginTop: 8, width: '100%', fontWeight: 500, transition: 'all 0.15s' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  topbar: { background: 'white', borderBottom: '1px solid #e2e8f0', padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' },
  breadcrumb: { fontSize: 14, color: '#0f172a', fontWeight: 600 },
  content: { flex: 1, padding: 28, overflowY: 'auto' as const, paddingBottom: 120 },
};

interface Props {
  children: React.ReactNode;
  user: any;
  signOut: () => void;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

const ADMIN_EMAILS = ['jordan@firstoinvest.com'];

export default function Layout({ children, user, signOut }: Props) {
  const location = useLocation();
  const navigate = useNavigate();

  // Only admins see the Admin section (AI Governance, Security, Admin)
  const userEmail = (user?.signInDetails?.loginId || user?.username || '').toLowerCase();
  const isAdmin = ADMIN_EMAILS.includes(userEmail);

  // Buyside lens: buyer (default) vs seller. Drives the nav set (Task 9) and a
  // top-bar toggle. Buyer nav is unchanged.
  const { lens, setLens } = useLens();
  const [lensSwitching, setLensSwitching] = useState(false);
  const baseGroups = navGroupsForLens(lens);
  const navGroups = isAdmin ? baseGroups : baseGroups.filter(g => g.label !== 'Admin');

  const handleLensChange = async (next: 'buyer' | 'seller') => {
    if (next === lens || lensSwitching) return;
    setLensSwitching(true);
    try {
      await setLens(next);
      // Send the user to a safe home for the new lens.
      navigate(next === 'seller' ? '/discovery' : '/dashboard');
    } catch {
      /* useLens reverts on failure */
    } finally {
      setLensSwitching(false);
    }
  };

  const currentPage = NAV.find(n => location.pathname.startsWith(n.path))?.label || '';

  const handlePrint = () => {
    window.print();
  };

  // Onboarding: scope per-user localStorage keys, and a small "?" help menu.
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => { setCurrentUser(userEmail); }, [userEmail]);

  // AI Assistant state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessages, setAiMessages] = useState<ChatMessage[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const aiInputRef = useRef<HTMLInputElement>(null);

  const handleAiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const question = aiInput.trim();
    if (!question || aiLoading) return;

    const userMsg: ChatMessage = { role: 'user', content: question, timestamp: Date.now() };
    setAiMessages(prev => [...prev, userMsg]);
    setAiInput('');
    setAiLoading(true);

    try {
      const res = await api.post<any>('/ai/ask', {
        question,
        context: currentPage.toLowerCase(),
        path: location.pathname,
      });
      const answer = res.answer || res.response || res.message || JSON.stringify(res);
      setAiMessages(prev => [...prev, { role: 'assistant', content: answer, timestamp: Date.now() }]);
    } catch (err: any) {
      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: `I couldn't process that request right now. (${err.message || 'API unavailable'})\n\nTip: You can ask me about opportunities, vendors, lifecycle status, deadlines, analysis results, and more.`,
        timestamp: Date.now(),
      }]);
    }
    setAiLoading(false);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleAiOpen = () => {
    setAiOpen(true);
    setTimeout(() => aiInputRef.current?.focus(), 100);
  };

  return (
    <div style={styles.shell} className="app-shell">
      {/* Accessibility: Global styles for focus, print, reduced motion */}
      <style>{`
        /* Skip to main content link */
        .skip-link {
          position: absolute;
          top: -40px;
          left: 0;
          background: #2563eb;
          color: white;
          padding: 8px 16px;
          z-index: 10000;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          border-radius: 0 0 4px 0;
        }
        .skip-link:focus {
          top: 0;
        }
        /* Global focus indicator - WCAG 2.4.7 */
        *:focus-visible {
          outline: 3px solid #2563eb !important;
          outline-offset: 2px !important;
        }
        /* Remove outline for mouse users */
        *:focus:not(:focus-visible) {
          outline: none !important;
        }
        /* Reduced motion - WCAG 2.3.3 */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
        /* High contrast mode support */
        @media (forced-colors: active) {
          .nav-link-active {
            border: 2px solid ButtonText;
          }
        }
        @media print {
          body { margin: 0; padding: 0; }
          .no-print, .ai-assistant-bar { display: none !important; }
          .app-sidebar { display: none !important; }
          .app-topbar { display: none !important; }
          .app-main { margin: 0 !important; padding: 0 !important; overflow: visible !important; }
          .app-content {
            padding: 0 !important;
            overflow: visible !important;
            height: auto !important;
          }
          .app-shell { display: block !important; }
          @page { margin: 0.6in; size: letter; }
        }
      `}</style>

      {/* Skip to main content - WCAG 2.4.1 */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Sidebar navigation - WCAG landmark */}
      <aside style={styles.sidebar} className="app-sidebar" aria-label="Primary navigation sidebar">
        <div style={styles.logo}>
          <div style={styles.logoTitle} aria-hidden="true">FTI Procurement</div>
          <div style={styles.logoSub} aria-hidden="true">Intelligence Platform</div>
        </div>
        <nav style={styles.nav} aria-label="Main navigation">
          {navGroups.map((group, groupIdx) => (
            <div key={group.label} style={{ marginBottom: 4 }}>
              {groupIdx > 0 && (
                <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 14px' }} role="separator" />
              )}
              <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 0.8, padding: '8px 14px 4px', userSelect: 'none' }} aria-hidden="true">
                {group.label}
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} role="list" aria-label={`${group.label} navigation`}>
                {group.items.map(item => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      data-tour={`nav-${item.path.replace(/^\//, '').replace(/\//g, '-')}`}
                      style={({ isActive }) => ({ ...styles.navLink, ...(isActive ? styles.navLinkActive : {}) })}
                      aria-current={location.pathname.startsWith(item.path) ? 'page' : undefined}
                      className={location.pathname.startsWith(item.path) ? 'nav-link-active' : ''}
                    >
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div style={styles.userBar}>
          <div aria-label="Current user">{user?.signInDetails?.loginId || user?.username || 'User'}</div>
          <button style={styles.signOut} onClick={() => { logActivity('logout', { detail: 'User signed out' }); try { sessionStorage.clear(); } catch {} signOut(); }} aria-label="Sign out of account">Sign Out</button>
        </div>
      </aside>

      {/* Main content area */}
      <div style={styles.main} className="app-main">
        {/* Top bar / breadcrumb - WCAG landmark */}
        <header style={styles.topbar} className="app-topbar" role="banner">
          <h1 style={{ ...styles.breadcrumb, margin: 0 }}>{currentPage}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Buyside lens (Option A): the lens is ASSIGNED by an admin (org_type)
                and locked for regular users — shown as a read-only badge. Admins keep
                an interactive toggle for testing/impersonation. */}
            {isAdmin ? (
              <div
                role="group"
                aria-label="Switch between Buyer and Seller views (admin)"
                title="Admin: preview the Buyer or Seller experience"
                style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', opacity: lensSwitching ? 0.6 : 1 }}
              >
                {(['buyer', 'seller'] as const).map(opt => (
                  <button
                    key={opt}
                    onClick={() => handleLensChange(opt)}
                    disabled={lensSwitching}
                    aria-pressed={lens === opt}
                    style={{
                      padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: lensSwitching ? 'default' : 'pointer',
                      background: lens === opt ? '#4338ca' : 'white',
                      color: lens === opt ? 'white' : '#475569',
                    }}
                  >
                    {opt === 'buyer' ? 'Buyer' : 'Seller'}
                  </button>
                ))}
              </div>
            ) : (
              <span
                aria-label={`Platform: ${lens === 'seller' ? 'Seller' : 'Buyer'}`}
                title="Your platform type is assigned by an administrator"
                style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 700, borderRadius: 8,
                  background: lens === 'seller' ? '#eef2ff' : '#f0fdf4',
                  color: lens === 'seller' ? '#4338ca' : '#166534',
                  border: `1px solid ${lens === 'seller' ? '#c7d2fe' : '#bbf7d0'}`,
                }}
              >
                {lens === 'seller' ? 'Seller' : 'Buyer'}
              </span>
            )}
            <button
              onClick={handlePrint}
              aria-label="Export current page as PDF"
              style={{
                padding: '7px 14px',
                background: '#f1f5f9',
                color: '#1e293b',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.15s',
              }}
            >
              <span aria-hidden="true">🖨️</span> Export
            </button>

            {/* Help / onboarding menu */}
            <div style={{ position: 'relative' }}>
              <button
                data-tour="help-button"
                onClick={() => setHelpOpen(o => !o)}
                aria-label="Help and onboarding"
                aria-expanded={helpOpen}
                style={{
                  width: 34, height: 34, borderRadius: 9999, background: '#eef2ff', color: '#4338ca',
                  border: '1px solid #c7d2fe', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >?</button>
              {helpOpen && (
                <>
                  <div onClick={() => setHelpOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1500 }} />
                  <div role="menu" style={{
                    position: 'absolute', right: 0, top: 40, zIndex: 1600, background: 'white',
                    border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                    width: 230, overflow: 'hidden',
                  }}>
                    {[
                      { label: '🧭  Take the guided tour', act: () => startTour() },
                      { label: '👋  Show welcome', act: () => openWelcome() },
                      { label: '🚀  Getting-started checklist', act: () => restoreChecklist() },
                    ].map((m, i) => (
                      <button key={i} role="menuitem"
                        onClick={() => { setHelpOpen(false); m.act(); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'white', border: 'none', borderBottom: i < 2 ? '1px solid #f3f4f6' : 'none', fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Main content region - WCAG 2.4.1 */}
        <main id="main-content" style={styles.content} className="app-content" role="main" aria-label={`${currentPage} page content`}>
          {children}
        </main>

        {/* AI Assistant Bar - WCAG compliant */}
        <aside className="ai-assistant-bar" aria-label="AI Assistant" style={{
          position: 'fixed',
          bottom: 0,
          left: 240,
          right: 0,
          zIndex: 1000,
          transition: 'all 0.3s ease',
        }}>
          {/* Chat Panel (expanded) */}
          {aiOpen && (
            <div
              role="dialog"
              aria-label="AI Assistant chat"
              aria-modal="false"
              style={{
                background: 'white',
                borderTop: '1px solid #e5e7eb',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
                maxHeight: 400,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Chat Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 16px',
                borderBottom: '1px solid #f3f4f6',
                background: '#f9fafb',
              }}>
                <h2 style={{ fontSize: 12, fontWeight: 600, color: '#374151', margin: 0 }}>
                  AI Assistant — Ask anything about your procurement data
                </h2>
                <div style={{ display: 'flex', gap: 8 }}>
                  {aiMessages.length > 0 && (
                    <button
                      onClick={() => setAiMessages([])}
                      aria-label="Clear chat history"
                      style={{ background: 'none', border: 'none', fontSize: 11, color: '#1e293b', cursor: 'pointer', padding: '4px 8px' }}
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => setAiOpen(false)}
                    aria-label="Close AI assistant"
                    style={{ background: 'none', border: 'none', fontSize: 16, color: '#374151', cursor: 'pointer', lineHeight: 1, padding: '4px 8px' }}
                  >
                    <span aria-hidden="true">✕</span>
                  </button>
                </div>
              </div>

              {/* Messages - aria-live for updates */}
              <div
                role="log"
                aria-live="polite"
                aria-label="Chat messages"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '12px 16px',
                  maxHeight: 260,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                {aiMessages.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: '#6b7280', fontSize: 12 }}>
                    <p>Ask me about opportunities, vendors, lifecycle status, deadlines, analysis results, or anything in the system.</p>
                    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                      {[
                        'What opportunities are due this week?',
                        'Show me vendors matching cybersecurity',
                        'What is the status of my lifecycles?',
                        'Summarize the latest AI analysis',
                        'Which opportunities need attention?',
                      ].map(q => (
                        <button
                          key={q}
                          onClick={() => { setAiInput(q); }}
                          aria-label={`Ask: ${q}`}
                          style={{ fontSize: 10, padding: '4px 10px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 12, cursor: 'pointer', color: '#1e293b' }}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {aiMessages.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <div
                      role={msg.role === 'assistant' ? 'status' : undefined}
                      aria-label={msg.role === 'user' ? 'You said' : 'AI response'}
                      style={{
                        maxWidth: '80%',
                        padding: '8px 12px',
                        borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                        background: msg.role === 'user' ? '#2563eb' : '#f3f4f6',
                        color: msg.role === 'user' ? 'white' : '#1f2937',
                        fontSize: 12,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }} role="status" aria-label="AI is thinking">
                    <div style={{ padding: '8px 12px', borderRadius: '12px 12px 12px 2px', background: '#f3f4f6', fontSize: 12, color: '#374151' }}>
                      Thinking...
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleAiSubmit} style={{
                display: 'flex',
                gap: 8,
                padding: '10px 16px',
                borderTop: '1px solid #f3f4f6',
                background: 'white',
              }}>
                <label htmlFor="ai-chat-input" className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
                  Ask AI a question
                </label>
                <input
                  id="ai-chat-input"
                  ref={aiInputRef}
                  value={aiInput}
                  onChange={e => setAiInput(e.target.value)}
                  placeholder="Ask about opportunities, vendors, deadlines, analysis..."
                  aria-label="Ask AI a question"
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    border: '1px solid #d1d5db',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                  disabled={aiLoading}
                />
                <button
                  type="submit"
                  disabled={aiLoading || !aiInput.trim()}
                  aria-label={aiLoading ? 'Sending question' : 'Send question to AI'}
                  style={{
                    padding: '10px 20px',
                    background: aiLoading || !aiInput.trim() ? '#93c5fd' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: aiLoading || !aiInput.trim() ? 'default' : 'pointer',
                  }}
                >
                  {aiLoading ? 'Sending...' : 'Send'}
                </button>
              </form>
            </div>
          )}

          {/* Toggle Button (always visible when closed) */}
          {!aiOpen && (
            <div style={{
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(12px)',
              borderTop: '1px solid #e2e8f0',
              padding: '10px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
              <button
                onClick={handleAiOpen}
                aria-label="Open AI assistant chat"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 18px',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 18 }}>✨</span>
                <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Ask AI anything about your procurement data...</span>
              </button>
            </div>
          )}
        </aside>
      </div>

      {/* Onboarding: first-run welcome + replayable guided tour */}
      <WelcomeModal />
      <GuidedTour />
    </div>
  );
}
